import { ErrorManagement } from '@printdeal/error-management/react';
import Api from '../Api';
import { AnonymousTokenNotAllowedError } from '../../errors/AnonymousTokenNotAllowedError';
import { getConfiguration } from '../../helpers/Configuration';
import { getStore } from '../../helpers/env';
import {
  CompanyProfileResponseRow,
  CompanySuggestionResponseRow,
} from '../../components/authentication/registration-suggest/types/BusinessSuggest';
import { CustomerAccount } from './CustomerAccount';

interface CompanySuggestV2Response {
  suggestions: CompanySuggestionResponseRow[];
}

interface CompanyProfileV2Response {
  companyDetails: CompanyProfileResponseRow[];
}

class CustomerApiService extends Api {
  async getAccounts(): Promise<CustomerAccount[]> {
    try {
      const { data } = await this.httpClient.get<CustomerAccount[]>(
        '/v1/customer/accounts',
        { blockAnonymous: true },
      );
      return data;
    } catch (error) {
      if (!(error instanceof AnonymousTokenNotAllowedError)) {
        ErrorManagement.exception({
          exception: error,
          tags: {
            fileLocation: 'CustomerApiService',
            featureFunction: 'getAccounts',
          },
          fingerprint: ['customer-api-service-get-accounts'],
        });
      }
      throw error;
    }
  }

  async addCustomerAsApiCustomer() {
    try {
      const { data } = await this.httpClient.patch('/v1/customer/api-user-access?action=enable');
      return data;
    } catch (error) {
      if (!(error instanceof AnonymousTokenNotAllowedError)) {
        ErrorManagement.exception({
          exception: error,
          tags: {
            fileLocation: 'CustomerApiService',
            featureFunction: 'addCustomerAsApiCustomer',
          },
          fingerprint: ['customer-api-service-add-customer-as-api-customer'],
        });
      }
      throw error;
    }
  }

  /**
   * Company.info search. Anonymous website JWT is allowed (do not set blockAnonymous).
   * Only valid for countryCode NL and store drukwerkdeal-nl.
   */
  async companySuggestV2(searchQuery: string): Promise<CompanySuggestV2Response> {
    const { data } = await this.httpClient
      .post<CompanySuggestV2Response>('/v2/suggestions/company/search', {
        searchQuery,
        countryCode: getConfiguration().officeAddress.countryCode,
        store: getStore(),
        type: 'all',
      })
      .catch(this.errorHandler);

    return data;
  }

  /**
   * Company.info profile. lookupId is a 12-digit branch id (preferred) or 8-digit KvK.
   */
  async companyProfileV2(lookupId: string): Promise<CompanyProfileV2Response> {
    const { data } = await this.httpClient
      .post<CompanyProfileV2Response>(`/v2/suggestions/company/${encodeURIComponent(lookupId)}`, {
        countryCode: getConfiguration().officeAddress.countryCode,
        store: getStore(),
      })
      .catch(this.errorHandler);

    return data;
  }
}

export default CustomerApiService;
